import { t } from 'i18next';

import { DialogActions } from '../actions/DialogActions';
import { UIHelper } from './UIHelper';
import { ArticleStore, ArticleItem } from '../stores/ArticleStore';
import { PriceStore } from '../stores/PriceStore';
import { ArticleListMaker } from './ArticleListMaker';
import { ProjectSettingsStore } from '../stores/ProjectSettingsStore';
import { ScreenshotHelper } from './ScreenshotHelper';
import { RELEASE_VERSION } from '../App';

import utils = egr.wcf.utils;
import { ImageSize, getImageSize } from '../submodules/utils/Image';

declare global {
    interface Window {
        pdfMake: any;
    }
}

export abstract class PDFMaker {
    private static AjaxBundleConfig: JQueryAjaxSettings = {
        url: 'scripts/pdfmake/pdfmake.bundle.js' + (utils.string.isNotNullOrEmpty(RELEASE_VERSION) ? '?v=' + RELEASE_VERSION : ''),
        dataType: 'script',
        cache: true
    };

    public static generatePDF(): Promise<any> {
        return PDFMaker.loadLib().then(PDFMaker.generatePDFDocument);
    }

    public static generatePDFAsBase64(): Promise<string> {
        return PDFMaker.loadLib().then(() => {
            return new Promise(async (resolve: (buffer: string) => void, reject: (error: Error) => void) => {
                PDFMaker.generatePDFDocument().then((pdf) => {
                    pdf.getBase64(resolve);
                });
            });
        });
    }

    public static generatePDFAsBuffer(): Promise<ArrayBuffer> {
        return PDFMaker.loadLib().then(() => {
            return new Promise(async (resolve: (buffer: ArrayBuffer) => void, reject: (error: Error) => void) => {
                PDFMaker.generatePDFDocument().then((pdf) => {
                    pdf.getBuffer(resolve, {binary: true});
                });
            });
        });
    }

    public static getFileName(): string {
        return t('Article List').replace(/[^a-z0-9]/gi, '_') + '.pdf';
    }

    /**
     * Note: this function does not trigger or wait for the update of the
     * property list. Use `DownloadHelper.downloadPDF` if the content has
     * to be synchronized first.
     */
    public static async downloadPDF(): Promise<void> {
        const doc: any = await PDFMaker.generatePDF();
        doc.download(PDFMaker.getFileName());
    }

    private static showLibError(resolve: () => void, reject: () => void): void {
        DialogActions.showErrorMessage(
            t('The PDF file could not be generated.'),
            () => { PDFMaker.loadLib().then(() => { resolve(); }); },
            reject
        );
    }

    private static loadLib(): Promise<void> {
        if ((window as any).pdfMake != null) {
            return Promise.resolve();
        }

        // load fonts using jQuery
        const tPromise: Promise<void> = new Promise<void>((resolve, reject) => {
            jQuery.ajax(PDFMaker.AjaxBundleConfig).done(() => {
                resolve();
            }).fail((pJqXHR: JQueryXHR) => {
                PDFMaker.showLibError(resolve, reject);
            });
        });
        return (tPromise);
    }

    private static generatePDFDocument = async (): Promise<any> => {
        const tArticleList: Array<ArticleItem> = ArticleStore.getState().articleList;
        const tShowPrices: boolean = UIHelper.showPrices();
        let tContent: Array<any>;
        if (tShowPrices) {
            tContent = [
                [
                    {text: t('Pos.'), style: 'tableHead'},
                    {text: t('Article image'), style: 'tableHead'},
                    {text: t('Description'), style: 'tableHead'},
                    {text: t('Quantity'), style: 'tableHead', alignment: 'right'},
                    {text: t('Single price'), style: 'tableHead', alignment: 'right'},
                    {text: t('Total price'), style: 'tableHead', alignment: 'right'}
                ]
            ];
        } else {
            tContent = [
                [
                    {text: t('Pos.'), style: 'tableHead'},
                    {text: t('Article image'), style: 'tableHead'},
                    {text: t('Description'), style: 'tableHead'},
                    {text: t('Quantity'), style: 'tableHead', alignment: 'right'}
                ]
            ];
        }
        let tPos: number = 1;
        let totalPrice: number = 0;
        let dontBreakRows: boolean = true;
        for (const tArticle of tArticleList) {
            if (tArticle.properties.article == null) {
                continue;
            }
            if (
                tArticle.properties.article.salesPrice != null &&
                utils.string.isNotNullOrEmpty(tArticle.properties.article.currency)
            ) {
                totalPrice += PriceStore.getPrice(tArticle.properties.article.salesPrice, tArticle.properties.article.currency);
            }
            let imageCell: any = {
                alignment: 'left',
                text: '',
                width: 48,
                height: 48,
                margin: [0, 5]
            };
            if (!egr.wcf.utils.string.isNullOrEmpty(tArticle.image)) {
                const imageSize: ImageSize | null = await getImageSize(tArticle.image);
                if (imageSize != null) {
                    imageCell = {
                        alignment: 'left',
                        image: tArticle.image,
                        width: imageSize.width,
                        height: imageSize.height,
                        margin: [0, 5]
                    };
                }
            }
            let features: Array<string> = [];
            if (utils.string.isNotNullOrEmpty(tArticle.properties.article.featureText)) {
                features = UIHelper.multilineTextToArray(tArticle.properties.article.featureText);
            }
            let longText: Array<string> = [];
            if (utils.string.isNotNullOrEmpty(tArticle.properties.article.longText)) {
                longText = UIHelper.multilineTextToArray(tArticle.properties.article.longText);
            }
            if ((features.length + longText.length) > 15) {
                dontBreakRows = false;
            }

            if (tShowPrices && tArticle.properties.article != null) {
                tContent.push([
                    {text: tPos.toString(), margin: [0, 5]},
                    imageCell,
                    {table: {
                        headerRows: 0,
                        widths: ['100%'],
                        body: [
                            [{
                                text:
                                    ArticleListMaker.getArticleHeader(tArticle.properties) +
                                    '\n' +
                                    ArticleListMaker.getManufacturerHeader(tArticle.properties) +
                                    '\n',
                                style: 'justbold'
                            }],
                            [{
                                text: tArticle.properties.article.longText
                            }],
                            [{
                                ul: features,
                            }]
                        ]
                        },
                        layout: 'noBorders',
                        margin: [0, 5]
                    },
                    {
                        text: '1', style: 'justbold', alignment: 'right',
                        margin: [0, 5]
                    },
                    {
                        text: ArticleListMaker.getPriceString(tArticle.properties, false),
                        alignment: 'right', style: 'justbold',
                        margin: [0, 5]
                    },
                    {
                        text: ArticleListMaker.getPriceString(tArticle.properties, true),
                        alignment: 'right', style: 'justbold',
                        margin: [0, 5]
                    }
                ]);
                tContent.push([
                    {text: '', margin: [0, -2.5]},
                    {text: '', margin: [0, -2.5]},
                    {text: '', margin: [0, -2.5]},
                    {text: '', margin: [0, -2.5]},
                    {text: '', margin: [0, -2.5]},
                    {text: '', margin: [0, -2.5]}
                ]);
            } else {
                tContent.push([
                    {text: tPos.toString(), margin: [0, 5]},
                    imageCell,
                    {
                        table: {
                            headerRows: 0,
                            widths: ['100%'],
                            body: [
                                [{text: (
                                    ArticleListMaker.getArticleHeader(tArticle.properties) + '\n' +
                                    ArticleListMaker.getManufacturerHeader(tArticle.properties) + '\n'
                                ), style: 'justbold'}],
                                [{
                                    text: tArticle.properties.article.longText
                                }],
                                [{ul: features}]
                            ]
                        },
                        layout: 'noBorders',
                        margin: [0, 5]
                    },
                    {text: '1', style: 'justbold', alignment: 'right', margin: [0, 5]}
                ]);
                tContent.push([
                    {text: '', margin: [0, -2.5]},
                    {text: '', margin: [0, -2.5]},
                    {text: '', margin: [0, -2.5]},
                    {text: '', margin: [0, -2.5]}
                ]);
            }
            tPos++;
        }
        if (tShowPrices) {
            tContent.push(
                [
                    '',
                    '',
                    {text: t('Price (excl. Tax)'), style: 'justbold'},
                    '',
                    '',
                    {text: PriceStore.getPriceString(totalPrice, ProjectSettingsStore.getState().currency), style: 'justbold', alignment: 'right'}
                ]
            );
        }
        let tWidths: Array<string>;
        if (tShowPrices) {
            tWidths = ['5%', '15%', '35%', '10%', '17%', '17%'];
        } else {
            tWidths = ['5%', '20%', '64%', '10%'];
        }
        /*
        margin: [left, top, right, bottom]
        margin: [horizontal, vertical]
        margin: equalLeftTopRightBottom
        */
        const articleListHeader: string = ArticleListMaker.getArticleListHeader();
        const docDefinition: any = {
            pageSize: 'A4',
            pageMargins: [ 50, 165, 50, 110 ],
            header: (page: number, pages: number): any => {
                return {
                    style: 'headerstyle',
                    table: {
                        headerRows: 0,
                        widths: [ '90%', '10%'],
                        body: [
                            [
                                {
                                    text: t('Article List'),
                                    style: 'headerheadline',
                                },
                                {
                                    text: page.toString() + '/' + pages.toString(),
                                    alignment: 'right',
                                    rowSpan: 2
                                }
                            ],
                            [
                                {
                                    text: articleListHeader,
                                    style: 'headerinfo',
                                    margin: [0, -2.2]
                                },
                                ''
                            ]
                        ]
                    },
                    layout: 'noBorders'
                };
            },
            footer: {
                text: ScreenshotHelper.getWatermarkText(),
                color: 'gray',
                fontSize: 8,
                margin: [50, 50, 50, 50]
            },
            content: [
                {
                    table: {
                        headerRows: 1,
                        widths: tWidths,
                        dontBreakRows: dontBreakRows,
                        body: tContent
                    },
                    layout: {
                        hLineWidth: (i: number, node: any): number => {
                            if (i === 0 || i === 1 || i === (tShowPrices ? (node.table.body.length - 2) : node.table.body.length)) {
                                return .5;
                            }
                            if (i === node.table.body.length) {
                                return 0;
                            }
                            return (i % 2 === 0 ? .25 : 0);
                        },
                        vLineWidth: (i: number, node: any): number => {
                            return 0;
                        },
                        hLineColor: (i: number, node: any): string => {
                            if (i === 0 || i === 1 || i === (tShowPrices ? (node.table.body.length - 2) : node.table.body.length)) {
                                return 'black';
                            }
                            if (i === node.table.body.length) {
                                return 'white';
                            }
                            return (i % 2 === 0 ? '#8c918c' : 'white');
                        },
                        vLineColor: (i: number, node: any): string => {
                                return 'black';
                        },
                        paddingTop: (i: number, node: any): number => {
                            if (i === node.table.body.length || i === 0) {
                                return 2;
                            }
                            return (i % 2 === 0 ? 0 : 2); // spacer
                        },
                        paddingBottom: (i: number, node: any): number => {
                            if (i === node.table.body.length || i === 0) {
                                return 2;
                            }
                            return (i % 2 === 0 ? 0 : 2); // spacer
                        }
                    }
                }
            ],
            defaultStyle: {
                font: 'OpenSansLight',
                fontSize: 10
            },
            styles: {
                headerstyle: {
                    margin: [ 50, 50, 50, 50 ]
                },
                justbold: {
                    bold: true
                },
                headerheadline: {
                    font: 'OpenSansLight',
                    fontSize: 24,
                    color: '#4f4f4f'
                },
                headerinfo: {
                    fontSize: 12
                },
                greyfont: {
                    color: '#8c918c'
                },
                link: {
                    color: '#8c918c',
                    decoration: 'underline'
                },
                tableHead: {
                    bold: true,
                    margin: [0, 7, 0, 7]
                }
            }
        };

        window.pdfMake.fonts = {
            OpenSansLight: {
                normal: 'opensans-light.ttf',
                bold: 'opensans-regular.ttf',
                italics: 'opensans-lightitalic.ttf',
                bolditalics: 'opensans-italic.ttf'
            }
        };
        return window.pdfMake.createPdf(docDefinition);
    };
}


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/utils/PDFMaker.ts